import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Calendar, Clock, MapPin, ChevronLeft, ChevronRight,
  Dumbbell, Shield, Activity, Loader2, Heart,
} from 'lucide-react';

const SESSION_STYLE = {
  TRAINING: { icon: Activity, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', label: 'Training' },
  TECHNICAL: { icon: Activity, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', label: 'Technical' },
  GK_TRAINING: { icon: Shield, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', label: 'GK Training' },
  MATCH: { icon: Shield, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Match' },
  GYM: { icon: Dumbbell, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Gym' },
  RECOVERY: { icon: Heart, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Recovery' },
  VBT: { icon: Dumbbell, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', label: 'VBT' },
  RTP: { icon: Shield, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30', label: 'RTP' },
  OTHER: { icon: Calendar, color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', label: 'Other' },
};

const STATUS_BADGE = {
  ASSIGNED: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  COMPLETED: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  PARTIAL: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  MISSED: 'bg-red-500/20 text-red-400 border-red-500/30',
  EXCUSED: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

export default function AthleteSchedulePage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [selectedSession, setSelectedSession] = useState(null);

  const weekEnd = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    return end;
  }, [weekStart]);

  const weekLabel = useMemo(() => {
    const startStr = weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const endStr = weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  }, [weekStart, weekEnd]);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/athlete/schedule', {
        params: { date_from: toISODate(weekStart), date_to: toISODate(weekEnd) },
      });
      setSchedule(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, weekStart, weekEnd]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(getMonday(new Date()));

  // Group schedule items by date
  const days = useMemo(() => {
    const map = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      map[toISODate(d)] = [];
    }
    for (const item of schedule) {
      if (map[item.date]) {
        map[item.date].push(item);
      }
    }
    return Object.entries(map);
  }, [schedule, weekStart]);

  const today = toISODate(new Date());

  return (
    <div className="space-y-5 fade-in" data-testid="athlete-schedule">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
          {t('My Schedule', 'Il Mio Programma')}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{t('Your weekly training schedule', 'Il tuo programma settimanale')}</p>
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevWeek} className="text-zinc-400 hover:text-zinc-100" data-testid="prev-week-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t('Prev', 'Prec')}
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-zinc-200" data-testid="week-label">{weekLabel}</span>
          <Button variant="outline" size="sm" onClick={goToday}
            className="border-zinc-700 text-zinc-400 hover:text-zinc-100 text-xs h-7 px-2" data-testid="today-btn">
            {t('Today', 'Oggi')}
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={nextWeek} className="text-zinc-400 hover:text-zinc-100" data-testid="next-week-btn">
          {t('Next', 'Succ')} <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
        </div>
      )}

      {/* Days */}
      {!loading && days.map(([date, items]) => {
        const isToday = date === today;
        const isPast = date < today;
        return (
          <div key={date} data-testid={`day-${date}`}>
            <div className={`flex items-center gap-2 mb-2 ${isToday ? 'text-amber-400' : isPast ? 'text-zinc-600' : 'text-zinc-400'}`}>
              {isToday && <div className="w-2 h-2 rounded-full bg-amber-400" />}
              <span className="text-sm font-semibold uppercase tracking-wider">{formatDate(date)}</span>
              {isToday && <span className="text-xs font-normal ml-1 text-amber-500/70">{t('Today', 'Oggi')}</span>}
            </div>

            {items.length === 0 ? (
              <div className={`py-3 px-4 text-sm rounded-sm border border-dashed ${isPast ? 'border-zinc-800/40 text-zinc-700' : 'border-zinc-800 text-zinc-600'}`}>
                {t('No sessions', 'Nessuna sessione')}
              </div>
            ) : (
              <div className="space-y-2">
                {items.map(item => {
                  const style = SESSION_STYLE[item.type] || SESSION_STYLE.OTHER;
                  const Icon = style.icon;
                  const statusClass = STATUS_BADGE[item.status] || STATUS_BADGE.ASSIGNED;
                  return (
                    <button
                      key={item.assignment_id}
                      type="button"
                      onClick={() => setSelectedSession(item)}
                      aria-label={`${item.title} — open details`}
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(245,158,11,0.15)' }}
                      className="w-full text-left rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                      data-testid={`session-card-${item.assignment_id}`}
                    >
                    <Card
                      className={`${style.bg} border ${style.border} ${isPast ? 'opacity-50' : ''} hover:border-amber-500/40 active:bg-amber-500/5 transition-colors`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className={`mt-0.5 p-1.5 rounded-sm ${style.bg}`}>
                              <Icon className={`w-4 h-4 ${style.color}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-zinc-100 text-sm">
                                {item.start_time ? <span className="text-amber-400/80">{item.start_time} – </span> : null}
                                {item.title}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-500">
                                <span className={`font-medium ${style.color}`}>{style.label}</span>
                                {item.duration_min && (
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.duration_min} min</span>
                                )}
                                {item.location && (
                                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>
                                )}
                                {item.team_code && (
                                  <span className="text-zinc-600">{item.team_code}</span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-xs text-zinc-500 mt-1.5 line-clamp-2">{item.description}</p>
                              )}
                            </div>
                          </div>
                          <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-semibold border ${statusClass}`}
                            data-testid={`status-${item.assignment_id}`}>
                            {item.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state for entire week */}
      {!loading && schedule.length === 0 && (
        <div className="text-center py-12" data-testid="empty-schedule">
          <Calendar className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">{t('No sessions scheduled this week', 'Nessuna sessione programmata questa settimana')}</p>
        </div>
      )}

      {/* Session detail modal (read-only for athletes) */}
      <Dialog open={!!selectedSession} onOpenChange={(o) => !o && setSelectedSession(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[85vh] overflow-y-auto" data-testid="athlete-session-detail">
          {selectedSession && (() => {
            const style = SESSION_STYLE[selectedSession.type] || SESSION_STYLE.OTHER;
            const Icon = style.icon;
            const statusClass = STATUS_BADGE[selectedSession.status] || STATUS_BADGE.ASSIGNED;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-zinc-100 flex items-start gap-2 pr-6">
                    <div className={`mt-1 p-1.5 rounded-sm ${style.bg} flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${style.color}`} />
                    </div>
                    <span className="text-base leading-snug" data-testid="session-modal-title">
                      {selectedSession.start_time ? <span className="text-amber-400/80 mr-1">{selectedSession.start_time}</span> : null}
                      {selectedSession.title}
                    </span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                    <span className={`font-medium ${style.color}`}>{style.label}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${statusClass}`}>
                      {selectedSession.status}
                    </span>
                    {selectedSession.duration_min && (
                      <span className="flex items-center gap-1 text-zinc-400"><Clock className="w-3 h-3" />{selectedSession.duration_min} min</span>
                    )}
                    {selectedSession.location && (
                      <span className="flex items-center gap-1 text-zinc-400"><MapPin className="w-3 h-3" />{selectedSession.location}</span>
                    )}
                    {selectedSession.team_code && (
                      <span className="text-zinc-600">{selectedSession.team_code}</span>
                    )}
                  </div>
                  {selectedSession.description && (
                    <div className="pt-2 border-t border-zinc-800">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">
                        {t('Description', 'Descrizione')}
                      </div>
                      <p
                        className="text-sm text-zinc-300 whitespace-pre-wrap break-words"
                        data-testid="session-modal-description"
                      >
                        {selectedSession.description}
                      </p>
                    </div>
                  )}
                  {selectedSession.notes && (
                    <div className="pt-2 border-t border-zinc-800">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">
                        {t('Notes from coach', 'Note dal coach')}
                      </div>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap break-words">
                        {selectedSession.notes}
                      </p>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
